import {
  Directive,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Optional,
} from "@angular/core";
import {
  AbstractControl,
  NG_VALIDATORS,
  ValidationErrors,
  Validator,
  ValidatorFn,
} from "@angular/forms";
import { MetaDirective } from "./meta.directive";
import { SubscriptionLike as ISubscription } from "rxjs";
import { ConstraintData, FieldMetaData } from "../../core/gb-api";
import {
  findMeta,
  isBlankInputValue,
  isEmptyInputValue,
} from "../functions/meta.functions";

@Directive({
  selector: "[ngModel]",
  providers: [
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => MetaValidatorDirective),
      multi: true,
    },
  ],
})
export class MetaValidatorDirective implements Validator, OnInit, OnDestroy {
  @Input()
  name: string;

  fieldMeta: FieldMetaData;

  onValidatorChange: () => void;

  subscription?: ISubscription;

  constructor(@Optional() private metaDirective?: MetaDirective) {}

  ngOnInit(): void {
    const that = this;
    if (this.metaDirective) {
      if (this.metaDirective.meta) {
        this.fieldMeta = findMeta(this.name, this.metaDirective.meta);
      }
      this.subscription = this.metaDirective.metaChanges.subscribe(
        (formMeta) => {
          that.fieldMeta = findMeta(that.name, formMeta);
        }
      );
    }
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  validate(c: AbstractControl): { [key: string]: any } {
    if (!this.fieldMeta) {
      return null;
    }
    const errors: ValidationErrors = {};
    if (this.fieldMeta.constraintData && this.fieldMeta.constraintData.length) {
      for (const constraintData of this.fieldMeta.constraintData) {
        Object.assign(errors, validate(c, constraintData));
      }
    }
    return errors;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }
}

function validate(
  c: AbstractControl,
  constraintData: ConstraintData
): ValidationErrors {
  switch (constraintData.validatorId) {
    case "ch.bedag.capitastra.common.meta.validation.constraint.NotNullFieldConstraintValidator":
      return notNullConstraintValidator(constraintData)(c);
    case "ch.bedag.capitastra.common.meta.validation.constraint.NotBlankFieldConstraintValidator":
      return notBlankConstraintValidator(constraintData)(c);
    case "ch.bedag.capitastra.common.meta.validation.constraint.RegexpConstraintValidator":
      return regexConstraintValidator(constraintData)(c);
    case "ch.bedag.capitastra.common.meta.validation.constraint.StringMaxLengthConstraintValidator":
      return lengthConstraintValidator(constraintData)(c);
    case "ch.bedag.capitastra.common.meta.validation.constraint.MinMaxLongConstraintValidator":
    case "ch.bedag.capitastra.common.meta.validation.constraint.MinMaxIntegerConstraintValidator":
      return minMaxConstraintValidator(constraintData)(c);
    default:
      throw new Error(`Unknown validation type: ${constraintData.validatorId}`);
  }
}

// TODO hier die angular validatoren verwenden (und die Meldungen nicht vom Backend kriegen)?
function notNullConstraintValidator(d: ConstraintData): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    return isEmptyInputValue(control.value)
      ? {
          required: {
            message: d.message,
          },
        }
      : null;
  };
}

function notBlankConstraintValidator(d: ConstraintData): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    return isBlankInputValue(control.value)
      ? {
          required: {
            message: d.message,
          },
        }
      : null;
  };
}

function regexConstraintValidator(d: ConstraintData): ValidatorFn {
  const message = d.message;
  const pattern = d.parameters["PATTERN"];
  const regexStr = "^(" + pattern + ")$";
  const regex = new RegExp(regexStr);
  return (control: AbstractControl): ValidationErrors | null => {
    const value: string = control.value;
    if (isEmptyInputValue(value)) {
      return null;
    }
    return regex.test(value)
      ? null
      : {
          regex: {
            message: message,
            requiredPattern: regexStr,
            actualValue: value,
          },
        };
  };
}

function lengthConstraintValidator(d: ConstraintData): ValidatorFn {
  const message = d.message;
  const length = Number(d.parameters["LENGTH"]);
  return (control: AbstractControl): ValidationErrors | null => {
    const value: string = control.value;
    if (isEmptyInputValue(value)) {
      return null;
    }
    return value.length <= length
      ? null
      : {
          length: {
            message: message,
            requiredLength: length,
            actualValue: value,
          },
        };
  };
}

function minMaxConstraintValidator(d: ConstraintData): ValidatorFn {
  const message = d.message;
  const minLength = Number(d.parameters["MIN_LENGTH"]);
  const maxLength = Number(d.parameters["MAX_LENGTH"]);
  return (control: AbstractControl): ValidationErrors | null => {
    if (isEmptyInputValue(control.value)) {
      return null;
    }
    const value: number = +control.value;
    return value >= minLength && value <= maxLength
      ? null
      : {
          minMaxLength: {
            message: message,
            minLength: minLength,
            maxLength: maxLength,
            actualValue: value,
          },
        };
  };
}
